import React, { useRef, useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';
import { di } from 'react-magnetic-di';
import PageIcon from '@atlaskit/icon/core/page';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { COMMAND_PALETTE_SECTIONS } from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY } from '@atlassian/jira-command-palette-common/src/constants.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import { CommandShortcuts } from '@atlassian/jira-command-palette-common/src/ui/command-shortcuts/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useTextSearchInputClause } from '@atlassian/jira-jql-utils/src/utils/text-search-input-clause/use-text-search-input-clause.tsx';
import messages from '../../../common/messages.tsx';
import type { OnSearchWrapper } from '../../../common/types.tsx';
import ListItemChild from '../../../common/ui/list-item-child/index.tsx';
import { StyledChild, StyledListItemChild } from '../../../common/ui/styled.tsx';
import TextField from '../../../common/ui/text-field/index.tsx';
import { useAST } from '../../../controllers/ast/index.tsx';
import TextFieldType from '../../../controllers/field-type/text/index.tsx';
import { useSendOptionAnalytics } from '../../../utils/send-option-analytics.tsx';
import { getTextSearchInputField } from '../../../common/constants.tsx';

type TextSearchInputProps = {
	onSearchWrapper?: OnSearchWrapper;
	onJourneyStart?: () => Attributes;
	shouldUseListMarkup?: boolean;
	placeholder?: string;
};

export const TextSearchInput = ({
	onSearchWrapper,
	onJourneyStart,
	shouldUseListMarkup,
	placeholder,
}: TextSearchInputProps): React.JSX.Element => {
	di(requestAnimationFrame);

	const intl = useIntl();
	const [ast] = useAST();
	const textSearchInputClause = useTextSearchInputClause(ast, getTextSearchInputField());

	const sendOptionAnalytics = useSendOptionAnalytics();

	const textSearchInputRef = useRef<HTMLInputElement>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const value = useMemo(
		() => TextFieldType.value(textSearchInputClause) || '',
		[textSearchInputClause],
	);

	const focusTextSearchInput = useCallback(() => {
		// This prevents the shortcut from being fed into the input
		if (!isNil(textSearchInputRef) && !isNil(textSearchInputRef.current)) {
			// This is the same way the global search input prevents
			// the shortcut from being fed into the input
			// See: src/packages/navigation-apps/atlassian-navigation/src/ui/integrations/keyboard-shortcuts/index.tsx
			requestAnimationFrame(() => {
				textSearchInputRef.current?.focus();
			});

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'pressed',
					actionSubject: 'keyboardShortcut',
				}),
				'jqlEditorBasicSearchInputKeyboardShortcut',
				{
					keyPressed: 'f',
					keyboardShortcut: true,
				},
			);
		}
	}, [createAnalyticsEvent]);

	const onTextFocus = useCallback(() => {
		if (!onJourneyStart) return;
		const analyticsEvent = createAnalyticsEvent({
			action: 'focused',
			actionSubject: 'input',
		});
		const attributes = onJourneyStart();
		fireUIAnalytics(analyticsEvent, 'refinementBarSearchInput', attributes);
	}, [createAnalyticsEvent, onJourneyStart]);

	const { getKeywords } = useCommandKeywords();

	const StyledListItemChildComponent = isVisualRefreshEnabled()
		? ListItemChild
		: StyledListItemChild;
	const FieldWrapper = shouldUseListMarkup ? StyledListItemChildComponent : StyledChild;

	const commandShortcuts = useMemo(
		() => ({
			f: {
				callback: focusTextSearchInput,
				registerInCommandPalette: {
					id: 'jql-builder-basic-jql-editor-text-picker-wrapper-focus-search-input',
					name: intl.formatMessage(messages.searchCurrentPage),
					keywords: getKeywords('pageSearchSynonyms'),
					components: {
						LeftIcon: () => (
							<PageIcon
								label={
									fg('deliveroo_compal_a11y_fixes')
										? ''
										: intl.formatMessage(messages.searchCurrentPage)
								}
								color={token('color.icon')}
								aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
							/>
						),
					},
					priority: COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY.searchOnCurrentPage,
					section: COMMAND_PALETTE_SECTIONS.quickActions,
					analytics: {
						action: 'searchCurrentPage',
					},
				},
			},
		}),
		[focusTextSearchInput, getKeywords, intl],
	);

	const onChange = useCallback(
		(keyword: string) => {
			onSearchWrapper && onSearchWrapper(TextFieldType.update(ast, textSearchInputClause, keyword));
		},
		[ast, onSearchWrapper, textSearchInputClause],
	);

	return (
		<FieldWrapper>
			<CommandShortcuts keyMap={commandShortcuts} />
			<TextField
				label={
					placeholder !== undefined && isVisualRefreshEnabled()
						? placeholder
						: intl.formatMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.refinementSearchIssueTermRefresh
									: messages.refinementSearch,
							)
				}
				value={value}
				onChange={onChange}
				onFocus={onJourneyStart ? onTextFocus : undefined}
				onSendOptionAnalytics={sendOptionAnalytics}
				inputRef={textSearchInputRef}
			/>
		</FieldWrapper>
	);
};
